import { redirect, type ParsedLocation } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { logAdminAccess } from "@/lib/admin-audit";

/**
 * Route guard for admin-only pages. Use inside `beforeLoad`.
 * Verifies an authenticated session AND an `admin` role in `user_roles`.
 * Redirects to /admin-login (preserving the intended URL) otherwise.
 *
 * Every role check (pass or fail) and every successful route visit is
 * recorded in `admin_access_audit`.
 */
export async function requireAdmin({ location }: { location: ParsedLocation }) {
  const redirectTo = location.href;
  if (typeof window === "undefined") {
    return {};
  }
  const { data: userRes, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userRes?.user) {
    throw redirect({ to: "/admin-login", search: { redirect: redirectTo } });
  }
  const userId = userRes.user.id;
  const email = userRes.user.email ?? null;

  // Self-heal admin role from the server-side allowlist before checking.
  await supabase.rpc("ensure_admin_role");

  const { data: isAdmin, error: roleErr } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });

  const granted = !roleErr && Boolean(isAdmin);

  // Log the role check itself (pass or fail).
  void logAdminAccess({
    userId,
    email,
    eventType: "role_check",
    route: location.pathname,
    roleGranted: granted,
    metadata: roleErr ? { rpc_error: roleErr.message } : undefined,
  });

  if (!granted) {
    throw redirect({ to: "/admin-login", search: { redirect: redirectTo } });
  }

  // Log the actual visit to the admin route.
  void logAdminAccess({
    userId,
    email,
    eventType: "route_visit",
    route: location.pathname,
    roleGranted: true,
  });

  return { userId };
}

/**
 * Variant of `requireAdmin` for the main /admin dashboard.
 * - Unauthenticated → redirect to /admin-login (preserves intended URL).
 * - Authenticated but NOT admin → returns `{ granted: false, userId, email }`
 *   so the route component can render a visible "Access denied" state
 *   instead of bouncing silently.
 * - Admin → returns `{ granted: true, userId, email }`.
 *
 * Audit logging matches `requireAdmin`.
 */
export async function requireAdminOrDeny({ location }: { location: ParsedLocation }) {
  const redirectTo = location.href;
  if (typeof window === "undefined") {
    return { granted: false as const, userId: null, email: null };
  }
  const { data: userRes, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userRes?.user) {
    throw redirect({ to: "/admin-login", search: { redirect: redirectTo } });
  }
  const userId = userRes.user.id;
  const email = userRes.user.email ?? null;

  // Self-heal admin role from the server-side allowlist before checking.
  await supabase.rpc("ensure_admin_role");

  const { data: isAdmin, error: roleErr } = await supabase.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  const granted = !roleErr && Boolean(isAdmin);

  void logAdminAccess({
    userId,
    email,
    eventType: "role_check",
    route: location.pathname,
    roleGranted: granted,
    metadata: roleErr ? { rpc_error: roleErr.message } : undefined,
  });

  if (granted) {
    void logAdminAccess({
      userId,
      email,
      eventType: "route_visit",
      route: location.pathname,
      roleGranted: true,
    });
  } else {
    void logAdminAccess({
      userId,
      email,
      eventType: "access_denied",
      route: location.pathname,
      roleGranted: false,
    });
  }

  return { granted, userId, email };
}
